import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  phone: z.string().trim().min(1, "Phone is required").max(20),
  email: z.string().trim().email("Invalid email address").max(255),
  zip: z.string().trim().min(1, "Zip code is required").max(10),
});

const goals = ["Weight", "Energy", "Mood", "Sleep", "Hair", "Skin", "Recovery"];

interface LearnMoreModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redirectToNuda?: boolean;
  nudaLink?: string;
  sourcePage?: 'home_page' | 'location_page' | string;
}

const LearnMoreModal = ({ open, onOpenChange, redirectToNuda = false, nudaLink = "https://nudalabs.com/nudavitae-assessment/", sourcePage = 'home_page' }: LearnMoreModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    zip: "",
  });
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleNextFromStep1 = () => {
    const result = formSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          newErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(newErrors);
      return;
    }
    setCurrentStep(2);
  };

  const toggleGoal = (goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  };

  const [leadId, setLeadId] = useState<string | null>(null);

  const saveLeadToDatabase = async () => {
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.from("consultation_leads").insert({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: `Zip: ${formData.zip.trim()} | Goals: ${selectedGoals.join(", ")}`,
        status: "new",
        orderly_clicked: false,
        nuda_clicked: false,
        source_page: sourcePage,
      }).select('id').single();

      if (error) throw error;
      if (data) {
        setLeadId(data.id);
      }
      toast.success("Your information has been saved!");
    } catch (error) {
      console.error("Error saving lead:", error);
      // Don't block the user from continuing even if save fails
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateLeadOrderlyClicked = async () => {
    if (!leadId) return;
    try {
      await supabase.from("consultation_leads")
        .update({ orderly_clicked: true })
        .eq('id', leadId);
    } catch (error) {
      console.error("Error updating orderly_clicked:", error);
    }
  };

  const updateLeadNudaClicked = async () => {
    if (!leadId) return;
    try {
      await supabase.from("consultation_leads")
        .update({ nuda_clicked: true })
        .eq('id', leadId);
    } catch (error) {
      console.error("Error updating nuda_clicked:", error);
    }
  };

  const handleNextFromStep2 = async () => {
    await saveLeadToDatabase();
    setCurrentStep(3);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset state when closing
      setCurrentStep(1);
      setFormData({ name: "", phone: "", email: "", zip: "" });
      setSelectedGoals([]);
      setErrors({});
      setLeadId(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-4xl p-0 overflow-hidden">
        {/* Progress Indicator */}
        <div className="px-6 pt-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentStep >= step ? "bg-wellness" : "bg-muted"
                }`}
              />
            ))}
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl font-bold text-center">
              {currentStep === 1 && "Tell Us About Yourself"}
              {currentStep === 2 && "What Are Your Goals?"}
              {currentStep === 3 && "Book Your Consultation"}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-6">
          {/* Step 1: Contact Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    placeholder="(555) 555-5555"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className={errors.phone ? "border-destructive" : ""}
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">{errors.phone}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">Zip Code</Label>
                  <Input
                    id="zip"
                    placeholder="12345"
                    value={formData.zip}
                    onChange={(e) => handleInputChange("zip", e.target.value)}
                    className={errors.zip ? "border-destructive" : ""}
                  />
                  {errors.zip && (
                    <p className="text-sm text-destructive">{errors.zip}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={handleNextFromStep1} className="font-semibold group">
                  Next: Select Goals
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Goals Selection */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <p className="text-muted-foreground text-center mb-4">
                Select your top 2-3 priorities
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {goals.map((goal) => (
                  <button
                    key={goal}
                    onClick={() => toggleGoal(goal)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 font-semibold ${
                      selectedGoals.includes(goal)
                        ? "border-wellness bg-wellness/10 text-wellness"
                        : "border-border bg-card hover:border-wellness/50 text-foreground"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {selectedGoals.includes(goal) && (
                        <Check className="w-4 h-4" />
                      )}
                      {goal}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="font-semibold"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={handleNextFromStep2} 
                  className="font-semibold group"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Next: See if you qualify"}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Book Consultation */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex justify-start">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="font-semibold"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </div>
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-wellness/10 rounded-full flex items-center justify-center mx-auto">
                  <Check className="w-8 h-8 text-wellness" />
                </div>
                <h2 className="text-2xl font-bold">Your Personalized Health Journey Starts Here!</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {redirectToNuda 
                    ? "Click below to complete your nudaVitae assessment with our partner Nuda Labs. You'll be redirected to their secure site to finish."
                    : "Click below to complete your consultation booking with our partner OrderlyMeds. You'll be redirected to their secure site to finish."
                  }
                </p>
                <Button
                  size="lg"
                  className="font-semibold text-lg px-8 py-6 group"
                  onClick={async () => {
                    if (redirectToNuda) {
                      await updateLeadNudaClicked();
                      window.open(nudaLink, "_blank");
                    } else {
                      await updateLeadOrderlyClicked();
                      window.open("https://orderlymeds.com/simplestart/?orid=466428", "_blank");
                    }
                  }}
                >
                  {redirectToNuda ? "Continue to Nuda Labs" : "Complete Booking"}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LearnMoreModal;
